/**
 * ChatMarkdown
 *
 * Componente leve para renderizar Markdown nas mensagens do assistente IA no chat.
 * Suporta: headers, listas, code blocks, tabelas, bold/italic, links, blockquotes.
 *
 * Design System:
 * - Dark mode obrigatório via classes dark:
 * - Estilização consistente com o design system TMF9
 *
 * @module components/ai
 * @since 2026-04-07
 * @issue TMF9-49875
 */

import { useMemo } from 'react';
import { marked, Renderer } from 'marked';
import { sanitizeHtml } from '@/utils/sanitize';
import { cn } from '@/lib/utils';

// Renderer customizado para links abrirem em nova aba
const renderer = new Renderer();

// Links abrem em nova aba com rel noopener
renderer.link = (
  { href, title, text }: { href: string; title?: string | null; text: string }
) => {
  const titleAttr = title ? ` title="${title}"` : '';
  return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
};

interface ChatMarkdownProps {
  /** Conteudo em Markdown a ser renderizado */
  content: string;
  /** Classes CSS adicionais no container */
  className?: string;
  /** Tamanho do texto base */
  size?: 'sm' | 'base';
}

/**
 * Renderiza Markdown de mensagens do assistente IA com estilizacao para chat.
 * Mensagens do usuario devem continuar usando <p> simples.
 */
export function ChatMarkdown({ content, className, size = 'sm' }: ChatMarkdownProps) {
  const html = useMemo(() => {
    if (!content) return '';
    const rawHtml = marked.parse(content, {
      renderer,
      gfm: true,
      breaks: true,
    }) as string;
    return sanitizeHtml(rawHtml);
  }, [content]);

  if (!html) return null;

  const textSize = size === 'sm' ? 'text-sm' : 'text-base';

  return (
    <div
      className={cn('chat-markdown', textSize, 'text-gray-700 dark:text-gray-300 leading-relaxed', className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default ChatMarkdown;
